import { PeriodicChecksPayload } from '~/types';
import { syncTypes } from '~/sqlite/syncDataTypes';

import { syncQueue } from './SyncQueue';

let periodicCheckQueueVersion = 0;
const periodicCheckQueueListeners = new Set<(version: number) => void>();

/** Bumped after each sync drain so open screens rescan the queue. */
export function notifyPeriodicCheckQueueChanged(): void {
    periodicCheckQueueVersion += 1;
    periodicCheckQueueListeners.forEach(listener => listener(periodicCheckQueueVersion));
}

export function getPeriodicCheckQueueVersion(): number {
    return periodicCheckQueueVersion;
}

export function subscribePeriodicCheckQueue(listener: (version: number) => void): () => void {
    periodicCheckQueueListeners.add(listener);
    return () => {
        periodicCheckQueueListeners.delete(listener);
    };
}

export type QueuedPeriodicCheck = PeriodicChecksPayload & {
    userId?: number | string
    updatedAt?: string
    operatingTime?: string
    operatingCount?: string
};

export async function listQueuedPeriodicChecks(userId: number): Promise<QueuedPeriodicCheck[]> {
    const rows = await syncQueue.listByUserId(userId);
    const queued: QueuedPeriodicCheck[] = [];
    for (const row of rows) {
        if (row.typeOfData !== syncTypes.ADD_CHECK_PERIOD) {
            continue;
        }
        try {
            queued.push(JSON.parse(row.data) as QueuedPeriodicCheck);
        } catch {
            // Malformed rows are dropped by SyncManager; ignore them here.
        }
    }
    return queued;
}

export async function getQueuedPeriodicCheck(
    userId: number,
    machineId: string | number,
    inspectionType: number,
): Promise<QueuedPeriodicCheck | null> {
    const queued = await listQueuedPeriodicChecks(userId);
    return queued.find(row =>
        String(row.machineId) === String(machineId)
        && Number(row.inspectionType) === Number(inspectionType)
    ) ?? null;
}

/** True when a completed check is still waiting to synchronize. Drafts stay editable. */
export async function hasPendingPeriodicCheckSync(
    userId: number,
    machineId: string | number,
    inspectionType: number,
): Promise<boolean> {
    const queued = await getQueuedPeriodicCheck(userId, machineId, inspectionType);
    return !!queued && !queued.isDraft;
}
